import React, { FC, useState } from 'react';
import { Collapse } from '@mui/material';
import classNames from 'classnames';
import toast from 'react-hot-toast';
import axios, { AxiosError } from 'axios';
import { useProfessions } from '../../hooks/useProfessions';
import { IProfession } from '../../models/Profession.interface';
import axiosInstant from '../../api';
import { SubSubProfessionsEndpoint } from '../../api/endpoints';
import { ActionIconButton, getProfessionColor } from '../professions/constants';
import { AddListItem } from '../professions/AddListItem';
import { AreaOfFocusListItem, IAreaOfFocus } from './AreaOfFocusListItem';
import { IconChevronDown } from '../../utils/icons';
import { useThemeTokens } from '../../hooks/useThemeTokens';

interface Props {
  profession: IProfession;
  areasOfFocus: IAreaOfFocus[];
}

// Collapsible card: one profession (read-only header — professions are managed
// on the Professions page), expanding to add/edit/delete the Areas of Focus
// scoped to it. Mirrors ProfessionListItem's sub-profession handling.
export const AreaOfFocusProfessionItem: FC<Props> = ({ profession, areasOfFocus: initial }) => {
  const { getProfessions } = useProfessions();
  const [open, setOpen] = useState(false);
  const [areas, setAreas] = useState<IAreaOfFocus[]>(initial);
  const [newOne, setNewOne] = useState<number | undefined>();
  const { mode } = useThemeTokens();
  const color = getProfessionColor(profession.id, mode === 'dark');

  const handleAreaUpdate = (areaId: number, newItem: IAreaOfFocus | null) => {
    const next = [...areas];
    const index = next.findIndex((a) => a.id === areaId);
    if (index === -1) return;
    if (newItem === null) {
      next.splice(index, 1);
    } else {
      next[index] = newItem;
      next.sort((a, b) => a.name.localeCompare(b.name));
    }
    setAreas(next);
    getProfessions();
  };

  const handleAddArea = async (newItem: { name: string }) => {
    try {
      const response = await axiosInstant.post(SubSubProfessionsEndpoint(profession.id), newItem);
      const { data } = response;
      setNewOne(data.id);
      const next = [...areas, data];
      next.sort((a, b) => a.name.localeCompare(b.name));
      setAreas(next);
      if (!open) setOpen(true);
      getProfessions();
      toast.success(`Area of focus '${newItem.name}' added!`);
      return '';
    } catch (e) {
      const error = e as AxiosError;
      if (axios.isAxiosError(error)) {
        console.error(error.response?.data);
        toast.error(error.response?.data as string);
        return error.response?.data as string;
      }
      console.error(error);
      toast.error('Failed to create new area of focus');
      return 'Failed to create new area of focus';
    }
  };

  return (
    <div
      className={classNames(
        'bg-surface border border-border-token rounded-[10px] overflow-hidden transition-all hover:border-[#D5D0C8]',
        open && 'shadow-[shadow:var(--sh-1)]',
      )}
    >
      <div
        className="flex items-center gap-2.5 px-4 py-3.5 cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        <div className="w-2 h-2 rounded-[3px] shrink-0" style={{ background: color }} />
        <span className="flex-1 min-w-0 truncate text-base text-text-1">{profession.name}</span>

        {areas.length > 0 && (
          <span className="text-[10.5px] font-semibold text-text-2 bg-surface-2 px-2 py-0.5 rounded-[10px] shrink-0">
            {areas.length}
          </span>
        )}

        <div className="flex items-center gap-0.5 ml-auto shrink-0" onClick={(e) => e.stopPropagation()}>
          <ActionIconButton title={open ? 'Collapse' : 'Expand'} onClick={() => setOpen(!open)}>
            <IconChevronDown open={open} />
          </ActionIconButton>
        </div>
      </div>

      <Collapse in={open} timeout="auto">
        <div className="border-t border-border-token pb-1.5">
          {areas.map((area) => (
            <AreaOfFocusListItem
              key={area.id}
              areaOfFocus={area}
              parentColor={color}
              onUpdate={handleAreaUpdate}
              isNew={newOne === area.id}
            />
          ))}
          <AddListItem
            label="Add Area of Focus"
            parentColor={color}
            placeholder="Area of focus name…"
            onSave={handleAddArea}
          />
        </div>
      </Collapse>
    </div>
  );
};
